import React, { useEffect, useState } from 'react';
import { useAdminStore } from '../stores/adminStore';
import type { AdminUser } from '../../../types';

export const UserManagement: React.FC = () => {
  const {
    users,
    loading,
    usersPage,
    usersTotalPages,
    loadUsers,
    updateUser
  } = useAdminStore();

  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    nickname: string;
    email: string;
    isAdmin: boolean;
  }>({ nickname: '', email: '', isAdmin: false });

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleEditUser = (user: AdminUser) => {
    setEditingUser(user.id);
    setEditForm({
      nickname: user.nickname || '',
      email: user.email || '',
      isAdmin: user.isAdmin
    });
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditForm({ nickname: '', email: '', isAdmin: false });
  };

  const handleSaveEdit = async (userId: string) => {
    try {
      await updateUser(userId, editForm);
      setEditingUser(null);
    } catch (error) {
      // 错误会显示在AdminPanel的错误提示中
    }
  };

  const handlePageChange = (page: number) => {
    loadUsers(page);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '从未';
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const StatusBadge: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      isAdmin
        ? 'bg-red-100 text-red-800'
        : 'bg-gray-100 text-gray-800'
    }`}>
      {isAdmin ? '管理员' : '普通用户'}
    </span>
  );

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">用户管理</h3>
          <p className="text-sm text-gray-500 mt-1">
            共 {users.length} 个用户，第 {usersPage} 页 / 共 {usersTotalPages} 页
          </p>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {users.map((user) => (
            <li key={user.id} className="px-6 py-4">
              {editingUser === user.id ? (
                // Edit Mode
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      <img
                        className="h-10 w-10 rounded-full"
                        src={user.avatar || `https://ui-avatars.com/api/?name=${user.name}&background=random`}
                        alt={user.name}
                      />
                    </div>
                    <div className="flex-1 grid grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">ID: {user.id.slice(0, 8)}...</p>
                      </div>
                      <div>
                        <input
                          type="text"
                          value={editForm.nickname}
                          onChange={(e) => setEditForm(prev => ({ ...prev, nickname: e.target.value }))}
                          placeholder="昵称"
                          className="block w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="邮箱"
                          className="block w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editForm.isAdmin}
                            onChange={(e) => setEditForm(prev => ({ ...prev, isAdmin: e.target.checked }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">管理员</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleSaveEdit(user.id)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      保存
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <img
                        className="h-10 w-10 rounded-full"
                        src={user.avatar || `https://ui-avatars.com/api/?name=${user.name}&background=random`}
                        alt={user.name}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <StatusBadge isAdmin={user.isAdmin} />
                      </div>
                      <div className="mt-1 text-sm text-gray-500 space-y-1">
                        {user.nickname && (
                          <p>昵称: {user.nickname}</p>
                        )}
                        {user.email && (
                          <p>邮箱: {user.email}</p>
                        )}
                        <div className="flex items-center space-x-4 text-xs">
                          <span>团队数: {user.teamCount}</span>
                          <span>任务数: {user.taskCount}</span>
                          <span>创建时间: {formatDate(user.createdAt)}</span>
                          <span>最后活跃: {formatDate(user.lastActiveAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      编辑
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Pagination */}
      {usersTotalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            显示第 {(usersPage - 1) * 20 + 1} - {Math.min(usersPage * 20, users.length)} 项
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(usersPage - 1)}
              disabled={usersPage <= 1 || loading}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上一页
            </button>
            <span className="px-3 py-2 text-sm font-medium text-gray-700">
              {usersPage} / {usersTotalPages}
            </span>
            <button
              onClick={() => handlePageChange(usersPage + 1)}
              disabled={usersPage >= usersTotalPages || loading}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一页
            </button>
          </div>
        </div>
      )}

      {users.length === 0 && !loading && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">暂无用户</h3>
          <p className="mt-1 text-sm text-gray-500">系统中还没有用户。</p>
        </div>
      )}
    </div>
  );
};